import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import dayjs from "dayjs";
import { listQbQuotes, deleteQbQuote } from "../../api/quotes";
import { listLocations } from "../../api/locations";
import { listSeasons } from "../../api/seasons";
import { formatMoney } from "../../utils/money";
import NewQuoteDialog from "./NewQuoteDialog";

const STATUSES = ["DRAFT", "SENT", "ACCEPTED", "EXPIRED", "CANCELLED"];

const statusColor = (s) => {
  switch (s) {
    case "ACCEPTED": return "success";
    case "SENT": return "info";
    case "EXPIRED":
    case "CANCELLED": return "default";
    default: return "warning";
  }
};

export default function QuoteList({ onOpen }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [locationId, setLocationId] = useState("");
  const [seasonId, setSeasonId] = useState("");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [createdByName, setCreatedByName] = useState("");
  const [locations, setLocations] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [newOpen, setNewOpen] = useState(false);

  useEffect(() => {
    listLocations({ activeOnly: false }).then((d) => setLocations(d || [])).catch(() => {});
    listSeasons({ activeOnly: false }).then((d) => setSeasons(d || [])).catch(() => {});
  }, []);

  const fetchAll = useCallback(() => {
    setLoading(true);
    setError("");
    listQbQuotes({
      status: status || undefined,
      locationId: locationId || undefined,
      seasonId: seasonId || undefined,
      search: search.trim() || undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
    })
      .then((d) => setRows(Array.isArray(d) ? d : []))
      .catch((e) => setError(e.message || "Failed to load quotes"))
      .finally(() => setLoading(false));
  }, [status, locationId, seasonId, search, fromDate, toDate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDelete = async (e, row) => {
    e.stopPropagation();
    if (!window.confirm(`Delete ${row.status.toLowerCase()} quote for "${row.clientName}"? This can't be undone.`)) return;
    try {
      await deleteQbQuote(row.id);
      fetchAll();
    } catch (err) {
      setError(err.message || "Delete failed");
    }
  };

  const locationName = useMemo(() => {
    const m = {};
    locations.forEach((l) => { m[l.id] = l.name; });
    return m;
  }, [locations]);
  const seasonName = useMemo(() => {
    const m = {};
    seasons.forEach((s) => { m[s.id] = s.name; });
    return m;
  }, [seasons]);

  const creatorOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => { if (r.createdByName) set.add(r.createdByName); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const visibleRows = useMemo(
    () => (createdByName ? rows.filter((r) => r.createdByName === createdByName) : rows),
    [rows, createdByName],
  );

  return (
    <Box>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }} sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Dynamic Quotes</Typography>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Created by</InputLabel>
          <Select label="Created by" value={createdByName} onChange={(e) => setCreatedByName(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {creatorOptions.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Location</InputLabel>
          <Select label="Location" value={locationId} onChange={(e) => setLocationId(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {locations.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Season</InputLabel>
          <Select label="Season" value={seasonId} onChange={(e) => setSeasonId(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {seasons.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField size="small" label="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
        <TextField size="small" label="From" type="date" InputLabelProps={{ shrink: true }} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        <TextField size="small" label="To" type="date" InputLabelProps={{ shrink: true }} value={toDate} onChange={(e) => setToDate(e.target.value)} />
        <Button startIcon={<RefreshIcon />} onClick={fetchAll}>Refresh</Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewOpen(true)}>New Quote</Button>
      </Stack>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Number</TableCell>
              <TableCell>Created by</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Season</TableCell>
              <TableCell align="right">Nights</TableCell>
              <TableCell align="right">Students</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell />
              <TableCell>Created</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={12} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : visibleRows.length === 0 ? (
              <TableRow><TableCell colSpan={12} align="center">No quotes match these filters.</TableCell></TableRow>
            ) : visibleRows.map((row) => (
              <TableRow
                key={row.id} hover
                sx={{ cursor: "pointer" }}
                onClick={() => onOpen?.(row.id)}
              >
                <TableCell>{row.quoteNumber || "(draft)"}</TableCell>
                <TableCell>{row.createdByName || ""}</TableCell>
                <TableCell>{row.clientName}</TableCell>
                <TableCell>
                  <Chip size="small" label={row.status} color={statusColor(row.status)} />
                </TableCell>
                <TableCell>{locationName[row.locationId] || row.locationId}</TableCell>
                <TableCell>{seasonName[row.seasonId] || row.seasonId}</TableCell>
                <TableCell align="right">{row.nights}</TableCell>
                <TableCell align="right">{row.studentPax}</TableCell>
                <TableCell align="right">{formatMoney(row.totalPrice)}</TableCell>
                <TableCell>
                  {row.marginBelowThreshold && (
                    <Tooltip title="Margin below threshold">
                      <WarningAmberIcon fontSize="small" color="warning" />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>{row.createdAt ? dayjs(row.createdAt).format("DD/MM/YYYY") : ""}</TableCell>
                <TableCell align="right">
                  {(row.status === "DRAFT" || row.status === "CANCELLED") && (
                    <Tooltip title={`Delete ${row.status.toLowerCase()} quote`}>
                      <IconButton size="small" onClick={(e) => handleDelete(e, row)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <NewQuoteDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        locations={locations}
        seasons={seasons}
        onCreated={(quote) => {
          setNewOpen(false);
          if (quote?.id) onOpen?.(quote.id);
          else fetchAll();
        }}
      />
    </Box>
  );
}
